import { SortParam } from 'src/lib/services/api-query-params';

/**
 * Default sort order for request lists.
 * Priority (highest to lowest), then submitted_at (newest first), then id (deterministic).
 * The 'id' sort ensures consistent ordering when priority and submitted_at are equal.
 */
export const DEFAULT_REQUEST_SORTS: SortParam[] = [
  { id: 'priority', desc: true },
  { id: 'submitted_at', desc: true },
  { id: 'id', desc: true },
];

/**
 * Ensure DEFAULT_REQUEST_SORTS are always present in the given sorts array.
 * User sorts take precedence; defaults fill in as tiebreakers so that
 * priority ordering is never lost when other sorts or filters are active.
 */
export const withDefaultRequestSorts = (sorts?: SortParam[]): SortParam[] => {
  if (!sorts || sorts.length === 0) {
    return [...DEFAULT_REQUEST_SORTS];
  }

  const existing = new Set(sorts.map((s) => s.id));
  const missing = DEFAULT_REQUEST_SORTS.filter((s) => !existing.has(s.id));

  return missing.length > 0 ? [...sorts, ...missing] : sorts;
};

/**
 * True when `sorts` is exactly the navigation-level default
 * ({@link DEFAULT_REQUEST_SORTS}) rather than a user-applied column sort.
 * The Priority Queue tab uses this to drop a carried-over default sort so the
 * server-side priority-queue order isn't suppressed: a default sort reaches a
 * list via `defaultSortFilter` links (e.g. the company "Requests" button) and
 * persists across tab switches. A genuine single-column user sort has a
 * different signature, so it returns false and the user's sort is kept.
 */
export const isDefaultRequestSorts = (sorts?: SortParam[]): boolean =>
  !!sorts &&
  sorts.length === DEFAULT_REQUEST_SORTS.length &&
  sorts.every(
    (s, i) => s.id === DEFAULT_REQUEST_SORTS[i].id && !!s.desc === !!DEFAULT_REQUEST_SORTS[i].desc,
  );

/**
 * Resolve the sorts to send for a request list scoped to `status` (a tab on
 * the List view, a column on the Kanban board).
 *
 * The backend's priority-queue order (urgency placement → client drag-drop
 * `sort`, RequestsTableFilter.php) only runs when `sorts` is empty, so the
 * queued scope must NOT receive the navigation-level default sorts that
 * `defaultSortFilter` links put in the URL — sending them suppresses the
 * queue order and the admin/editor view drifts from the client's (#2383,
 * AAG #2478 list + kanban re-open). A genuine user column sort has a
 * different signature and is passed through, overriding the queue order,
 * which matches the customer flow. Every other scope gets the defaults
 * appended as tiebreakers as before.
 */
export const resolveQueueAwareSorts = (
  status: string | undefined,
  sorts?: SortParam[],
): SortParam[] => {
  if (status !== 'queued') {
    return withDefaultRequestSorts(sorts);
  }

  return isDefaultRequestSorts(sorts) ? [] : sorts ?? [];
};
